import React, { useState, useContext } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogContent, DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, CircularProgress, Alert,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useMutation, gql } from '@apollo/client';
import UserForm from '../components/UserForm';
import AuthContext from '../contexts/AuthContext';
import { StyledDialogTitle, StyledTableContainer } from '../components/StyledComponents';

// GraphQL queries et mutations
const GET_VISITORS = gql`
  query GetVisitors {
    users(role: "VISITOR") {
      id
      email
      username
      firstName
      lastName
      role
      phoneNumber
      otpEnabled
    }
  }
`;


const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      success
      message
    }
  }
`;

function Visitors() {
  const { user } = useContext(AuthContext);
  
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // État pour l'utilisateur sélectionné
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  
  // État pour les messages d'erreur
  const [errorMessage, setErrorMessage] = useState('');

  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  
  // Récupérer la liste des visiteurs
  const { data: visitorsData, loading: visitorsLoading, error: visitorsError, refetch: refetchVisitors } = useQuery(GET_VISITORS, {
    skip: !['ADMIN', 'MANAGER', 'RH'].includes(user?.role)
  });

  // Filtrer les visiteurs en fonction de la recherche
  const filteredVisitors = React.useMemo(() => {
    if (!visitorsData?.users) return [];
    
    let filtered = visitorsData.users;

    // Filtrer par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(visitor => 
        visitor.firstName.toLowerCase().includes(query) ||
        visitor.lastName.toLowerCase().includes(query) ||
        visitor.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [visitorsData, searchQuery]);

  // Mutation pour supprimer un visiteur
  const [deleteUser, { loading: deleteLoading }] = useMutation(DELETE_USER, {
    onCompleted: (data) => {
      if (data.deleteUser.success) {
        setOpenDeleteDialog(false);
        setSelectedVisitor(null);
        setErrorMessage('');
        refetchVisitors();
      } else {
        setErrorMessage(data.deleteUser.message || "Une erreur est survenue lors de la suppression");
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression:", error);
      setErrorMessage(`Erreur: ${error.message}`);
    }
  });
  
  // Gérer l'ouverture du dialogue de création
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };
  
  // Gérer la fermeture du dialogue de création
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  
  // Gérer l'ouverture du dialogue de modification
  const handleOpenEditDialog = (visitor) => {
    setSelectedVisitor(visitor);
    setOpenEditDialog(true);
  };
  
  // Gérer la fermeture du dialogue de modification
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedVisitor(null);
  };
  
  // Gérer l'ouverture du dialogue de suppression
  const handleOpenDeleteDialog = (visitor) => {
    setSelectedVisitor(visitor);
    setOpenDeleteDialog(true);
  };
  
  // Gérer la fermeture du dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedVisitor(null);
  };
  
  // Gérer la suppression d'un visiteur
  const handleDeleteVisitor = () => {
    if (selectedVisitor) {
      deleteUser({
        variables: {
          id: selectedVisitor.id
        }
      });
    }
  };
  
  // Gérer le succès de la création d'un visiteur
  const handleCreateSuccess = () => {
    setOpenCreateDialog(false);
    refetchVisitors();
  };
  
  // Gérer le succès de la modification d'un visiteur
  const handleEditSuccess = () => {
    setOpenEditDialog(false);
    setSelectedVisitor(null);
    refetchVisitors();
  };
  
  // Afficher un indicateur de chargement si les données sont en cours de chargement
  if (visitorsLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Afficher un message d'erreur si une erreur s'est produite
  if (visitorsError) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Erreur lors du chargement des visiteurs: {visitorsError.message}
          </Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Visiteurs
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <TextField
            fullWidth
            label="Rechercher"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            placeholder="Rechercher par nom, prénom ou email..."
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Nouveau Visiteur
          </Button>
        </Box>
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom complet</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVisitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucun visiteur trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredVisitors.map((visitor) => (
                  <TableRow key={visitor.id}>
                    <TableCell>{`${visitor.firstName} ${visitor.lastName}`}</TableCell>
                    <TableCell>{visitor.email}</TableCell>
                    <TableCell>{visitor.phoneNumber || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={visitor.otpEnabled ? 'Activé' : 'Désactivé'}
                        color={visitor.otpEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(visitor)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(visitor)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
      
      {/* Dialogue de création de visiteur */}
      <Dialog 
        open={openCreateDialog} 
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Créer un nouveau visiteur</StyledDialogTitle>
        <DialogContent>
          <UserForm 
            onSuccess={handleCreateSuccess} 
            defaultRole="VISITOR"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de modification de visiteur */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Modifier le visiteur</StyledDialogTitle>
        <DialogContent>
          <UserForm 
            onSuccess={handleEditSuccess} 
            initialData={selectedVisitor}
            isEdit={true}
            defaultRole="VISITOR"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de suppression de visiteur */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <DialogContent>
          {selectedVisitor && (
            <Typography>
              Êtes-vous sûr de vouloir supprimer le visiteur {selectedVisitor.firstName} {selectedVisitor.lastName} ?
            </Typography>
          )}
          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDeleteVisitor} 
            color="error"
            disabled={deleteLoading || !selectedVisitor}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Visitors;


